import {
    FetchState,
    InvestmentThreshold,
    InvestmentCustodian,
    FeatureFlags
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";
import { useDrivewealthAccountMigrationStore } from "@/shared/store/drivewealthAccountMigration";
import { templatedFetchKeyStore } from "./fetchKeyedPlugin";

const emptyThresholds = (): InvestmentThreshold => ({
    accountCreate: 0,
    moneyTransfer: 0,
    investmentProvider: InvestmentCustodian.None
});

const useSubStore = defineStore("investmentThreshold_fetchKeyed", () => {
    return templatedFetchKeyStore<InvestmentThreshold>(
        emptyThresholds,
        null,
        bankService.getBalanceThresholds
    );
});

export const useInvestmentThresholdStore = defineStore("investmentThreshold", {
    getters: {
        thresholdsLoading: () => useSubStore().fetchState === FetchState.Loading,
        thresholdsLoaded: () => useSubStore().fetchState === FetchState.Loaded,
        isDrivewealthInvestmentProvider (): boolean {
            const substore = useSubStore();

            return substore.investmentProvider === InvestmentCustodian.Drivewealth;
        },
        thresholdForAccountCreation (): number {
            const hsabInvestmentStore = useHsabInvestmentStore();
            const drivewealthAccountMigrationStore = useDrivewealthAccountMigrationStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const subStore = useSubStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;
            const hasLegacyInvestmentAccount: boolean = hsabInvestmentStore.hasLegacyInvestmentAccount;


            const disableInvestmentThreshold: boolean = (
                subStore.investmentProvider === InvestmentCustodian.Drivewealth &&
                isHsaBankCustodian &&
                drivewealthAccountMigrationStore.accountMigrationActive &&
                hasLegacyInvestmentAccount
            );

            if (disableInvestmentThreshold) {
                return 0;
            }

            return subStore.accountCreate;
        },
        thresholdForMoneyTransfer (): number {
            const subStore = useSubStore();

            return subStore.moneyTransfer;
        },
        investmentThreshold () {
            return (alreadyHasAccount?: boolean | any) => alreadyHasAccount
                ? this.thresholdForMoneyTransfer
                : this.thresholdForAccountCreation;
        }
    },
    actions: {
        async loadInvestmentThresholds (options?: { username?: string, partnerId?: number }): Promise<any> {
            return useSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});
